/* eslint-disable @typescript-eslint/no-explicit-any */
import { ZodObject } from 'zod';

import { AnyObject } from 'types';
import { omit } from 'utils/object';
import { PossibleSchema } from 'utils/validation';

/**
 * This is a best effort resolution to enable lazy default values
 * since most use cases do not require basic value: '' on all fields..
 *
 * WARNING: This is generated via the zod document helper AI and might be
 * not optimal. As its a throw-in script where we've tested every normal use case
 * this is acceptable
 */

function getTypeTag(s: PossibleSchema): string | undefined {
  // In v4, `_def.type` is typically a string tag like "string", "object", "default", etc.
  return (s as any)?._def?.type;
}

function unwrapForDefaults(initialRef: PossibleSchema): PossibleSchema {
  // Unwrap "pipeline"/"effects-like" wrappers that commonly appear in v4:
  // - default: handled in main fn (we want to stop there to use its defaultValue)
  // - optional/nullable: handled in main fn (stop there)
  // - pipe: unwrap to output side (or input side depending on your preference)
  // - preprocess/transform/refine: unwrap to inner schema
  //
  // NOTE: Tags vary slightly across minor versions; this is best-effort.
  let schema = initialRef;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const t = getTypeTag(schema);

    // Stop conditions: handled by main function
    if (t === 'default' || t === 'optional' || t === 'nullable') return schema;

    if (t === 'pipe') {
      // ZodPipeline: prefer output schema for defaults
      const out = (schema as any)._def?.out;

      if (out) {
        schema = out;
        continue;
      }

      return schema;
    }

    if (t === 'transform' || t === 'preprocess' || t === 'refinement') {
      // These wrappers typically store the inner schema under `_def.schema`
      const inner = (schema as any)._def?.schema;

      if (inner) {
        schema = inner;
        continue;
      }

      return schema;
    }

    return schema;
  }
}

export function toDefaultValues(schema: PossibleSchema): any {
  const refSchema = unwrapForDefaults(schema);

  const refType = getTypeTag(refSchema);

  // default
  if (refType === 'default') {
    return (refSchema as any)._def.defaultValue();
  }

  // optional vs nullable: pick semantics intentionally
  if (refType === 'optional') return undefined;
  if (refType === 'nullable') return null;

  // object
  if (refType === 'object') {
    // In v4, shape is usually a function: `_def.shape()`
    const shape =
      typeof (refSchema as any).shape === 'object'
        ? (refSchema as any).shape
        : (refSchema as any)._def?.shape?.();

    if (!shape || typeof shape !== 'object') return {};

    return Object.fromEntries(
      Object.entries(shape).map(([key, sub]) => [key, toDefaultValues(sub as PossibleSchema)]),
    );
  }

  // array
  if (refType === 'array') return [];

  // primitives
  if (refType === 'string') return '';
  if (refType === 'number') return 0;
  if (refType === 'boolean') return false;
  if (refType === 'date') return null;

  // enums
  if (refType === 'enum') {
    const options: unknown[] =
      (refSchema as any)._def?.values ?? (refSchema as any)._def?.entries ?? [];
    return options[0] ?? undefined;
  }

  if (refType === 'nativeEnum') {
    const e = (refSchema as any)._def?.values ?? (refSchema as any)._def?.enum;
    const values = e ? Object.values(e) : [];
    const first = values.find((v) => typeof v === 'string' || typeof v === 'number');
    return first ?? undefined;
  }

  // literal
  if (refType === 'literal') return (refSchema as any)._def?.value;

  // union: choose first option’s default (best-effort)
  if (refType === 'union') {
    const options: PossibleSchema[] = (refSchema as any)._def?.options ?? [];
    return options[0] ? toDefaultValues(options[0]) : undefined;
  }

  return undefined;
}

export function resolveDefaultValues(schema: PossibleSchema, providedDefaults: AnyObject = {}) {
  if (!(schema instanceof ZodObject)) return providedDefaults;

  return {
    ...providedDefaults,

    ...Object.fromEntries(
      Object.entries(omit(schema.shape, Object.keys(providedDefaults))).map(([key, subSchema]) => [
        key,
        toDefaultValues(subSchema as PossibleSchema),
      ]),
    ),
  };
}
